import {inject, Injectable} from '@angular/core';
import {catchError, defaultIfEmpty, map, Observable, of, switchMap} from 'rxjs';
import {ToastrService} from 'ngx-toastr';
import {ModalInputService} from '@brunata-angular/ui';
import {TRANSLATE} from '@brunata-angular/util';
import {LocationsConsumer} from '@shared/services/location.service';
import {ConsumerService} from './consumer.service';
import {EditConsumerModalComponent} from './components/edit-consumer-modal.component';

@Injectable({
    providedIn: 'root',
})
export class ConsumerEditService {
    private readonly modalInputService = inject(ModalInputService);
    private readonly consumerService = inject(ConsumerService);
    private readonly toastr = inject(ToastrService);
    private readonly translate = inject(TRANSLATE);

    /**
     * Opens the edit popup, saves the change and announces it. Emits once whether anything was
     * saved, so the caller knows to reload; false when the popup is cancelled or the save fails.
     */
    editWithModal(request: ConsumerEditRequest): Observable<boolean> {
        return this.modalInputService
            .open(EditConsumerModalComponent, request.consumer, {size: 'lg'})
            .closed$.pipe(
                switchMap(update =>
                    this.consumerService.updateConsumer(
                        request.buildingNo,
                        request.locationId,
                        request.consumer.consumerId,
                        update,
                    ),
                ),
                map(() => {
                    this.toastr.success(
                        this.translate('alerts_residentUpdateSuccess', 'online', 'residents'),
                    );
                    return true;
                }),
                // The error toast comes from the global ErrorInterceptor.
                catchError(() => of(false)),
                defaultIfEmpty(false),
            );
    }
}

export type ConsumerEditRequest = {
    buildingNo: string;
    locationId: string;
    consumer: LocationsConsumer;
};
